import { useState, useCallback, useMemo, useEffect } from 'react';

import { BackrsUser, appStrings } from '@backrs_ui/common-ui';

import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import {
  useFetchSuperCoachAssigneesQuery,
  useRequestEmulationTokenMutation,
} from '@backrs_ui/grow-ui/src/redux/superCoach/superCoach.api';
import { redirectToEmulation } from '@backrs_ui/grow-ui/src/utils/emulation';

import { Snackbar, Alert } from '@mui/material';

import { AssigneeEmulationPaletteUserItem } from './AssigneeEmulationPaletteUserItem';
import { CommandPalette } from '../CommandPalette';

export interface AssigneeEmulationPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const AssigneeEmulationPalette = ({ open, onClose }: AssigneeEmulationPaletteProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<BackrsUser | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // RTK Query hooks
  const [requestEmulationToken, { isLoading: isRequestingToken }] =
    useRequestEmulationTokenMutation();

  // Debounce the search value for API calls
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Reset state when the modal is closed
  useEffect(() => {
    if (!open) {
      setSearchValue('');
      setSelectedUser(null);
      setErrorMessage(null);
    }
  }, [open]);

  // Use the API hook with debounced search
  const {
    data: users = [],
    isLoading,
    error,
  } = useFetchSuperCoachAssigneesQuery(
    debouncedSearchValue.trim() ? { q: debouncedSearchValue.trim() } : undefined,
    {
      skip: !open, // Don't fetch when the palette is closed
    },
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setSelectedUser(null); // Reset selection when search changes
  }, []);

  const handleUserSelect = useCallback((user: BackrsUser) => {
    setSelectedUser(user);
  }, []);

  const handleEmulateUser = useCallback(async () => {
    if (selectedUser && !isRequestingToken) {
      try {
        const response = await requestEmulationToken(selectedUser.id.toString()).unwrap();

        redirectToEmulation(response);
      } catch (error) {
        console.error('Token request failed:', error);
        setErrorMessage(appStrings.assigneeEmulation.emulationFailed);
      }
    }
  }, [selectedUser, isRequestingToken, requestEmulationToken]);

  const renderUserItem = useCallback(
    (user: BackrsUser) => <AssigneeEmulationPaletteUserItem user={user} />,
    [],
  );

  const getUserKey = useCallback((user: BackrsUser) => user.id.toString(), []);

  const actions = useMemo(() => {
    return [
      {
        label: isRequestingToken
          ? appStrings.assigneeEmulation.emulateButtonLoading
          : appStrings.assigneeEmulation.emulateButton,
        onClick: handleEmulateUser,
        disabled: !selectedUser || isRequestingToken,
        variant: (selectedUser && !isRequestingToken ? 'contained' : 'outlined') as
          | 'contained'
          | 'outlined',
      },
    ];
  }, [selectedUser, handleEmulateUser, isRequestingToken]);

  const emptyStateMessage = useMemo(() => {
    if (error) {
      return appStrings.assigneeEmulation.loadFailed;
    }
    if (debouncedSearchValue.trim() && !isLoading) {
      return appStrings.assigneeEmulation.noUsersFound(debouncedSearchValue.trim());
    }

    return appStrings.assigneeEmulation.startTyping;
  }, [error, debouncedSearchValue, isLoading]);

  const handleCloseError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  return (
    <>
      <CommandPalette
        open={open}
        onClose={onClose}
        searchPlaceholder={appStrings.assigneeEmulation.searchPlaceholder}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        items={users}
        renderItem={renderUserItem}
        getItemKey={getUserKey}
        selectedItem={selectedUser}
        onItemSelect={handleUserSelect}
        loading={isLoading}
        emptyStateMessage={emptyStateMessage}
        actions={actions}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
